/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../../ts';
import { Skill, SkillType } from '../../../../../ts/skill';
import type { CreateAsyncThunk, SkillsSlice } from '../../../../../ts/state';
import { sendTransactions } from '../../../../actions/api/transactions';
import { setAutoSaveState } from '../../../../actions/store/actions/actions';
import { getGlobalVariables } from '../../../../actions/store/actions/selectors';
import { fetchEntities } from '../../../../actions/store/entities/actions';
import { getById } from '../../../../actions/store/entities/selectors';
import { getAllSystemVariables } from '../../../../actions/store/variables/selectors';
import { splitTextIntoExpressionFormat } from '../../../../actions/util/actionUtil';
import { noTopicReturnTranslations } from '../../../../actions/util/noTopicReturnTranslations';
import { yesNoTranslation } from '../../../../actions/util/sysTranslations';
import { selectIsNewIa } from '../../../../ia/store/instance/selectors';
import * as API from '../../../api/skills';
import { getErrorString } from '../../../util/errorHandler';
import { updatedObjectEvents } from '../../../util/trackers/segment';
import { selectAssistant } from '../../assistants/core-selectors';
import {
  isActionsDigressionReturnConfirmationEnabled,
  selectIsAccessUpdatesEnabled
} from '../../features/selectors';
import { addErrorMessage } from '../../messages/actions';
import { getActionSkillId, getAssistantId } from '../../session/selectors';
import { entityAdapter } from '../adapter';
import { selectSkillById, selectSkillLanguage } from '../core-selectors';

export const updateSkill = createAsyncThunk<
  Skill,
  Partial<Skill>,
  CreateAsyncThunk
>('skills/update', async (data, { dispatch, getState }) => {
  const { skill_id, assistant_id } = data;
  try {
    const new_ia = selectIsNewIa(getState());
    const assistant = selectAssistant(getState());
    const sessionAssistantId = getAssistantId(getState());
    // Due to a store bug auto creating skills with the language 'xx', we
    // must re-set the skill language to be that of the assistant
    if (assistant?.language) {
      data.language = assistant.language;
    }
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler new_ia on call to updateSkill
    const useNewIAUpdatedAccess =
      selectIsAccessUpdatesEnabled(getState()) && new_ia;
    // NOTE if an assistant-id is in the skill, use that one (may be different on create processing)
    const assistantId = assistant_id ? assistant_id : sessionAssistantId;
    const response = await API.updateSkill(
      data,
      { new_ia },
      useNewIAUpdatedAccess,
      assistantId
    );
    const state = getState();
    return mergeExistingData(data, state, response);
  } catch (error) {
    dispatch(addErrorMessage(error));
    updatedObjectEvents.skill({
      object: skill_id,
      resultValue: getErrorString(error),
      successFlag: false
    });
    throw error;
  }
});

export const updateActionSkillSettings = createAsyncThunk<
  Skill,
  { skill_id; skillSettings },
  CreateAsyncThunk
>('skills/updateActionSkillSettings', async (data, { dispatch, getState }) => {
  const { skill_id, skillSettings } = data;

  try {
    const skill = selectSkillById(getState(), skill_id);
    if (!skill) {
      return;
    }

    let systemSettings = skill.workspace?.system_settings ?? null;
    if (!systemSettings) {
      //old IA does not export the skill and uses include_audit which results in the
      //workspace details being stored in workspace_reference
      systemSettings = skill.workspace_reference?.system_settings ?? {};
    }
    let newSystemSettings = { ...systemSettings };
    const assistant = selectAssistant(getState());
    // There is currently a store bug that auto-creates all draft and live
    // skills with the language 'xx'. To get the correct language associated
    // with the skill, we must grab from the assistant
    const language = assistant?.language || selectSkillLanguage(getState());

    if (skillSettings.hasOwnProperty('spelling_auto_correct')) {
      newSystemSettings.spelling_auto_correct =
        skillSettings.spelling_auto_correct;
    }
    if (skillSettings.hasOwnProperty('disambiguation')) {
      if (skillSettings.disambiguation === null) {
        delete newSystemSettings.disambiguation;
      } else {
        const {
          enabled,
          prompt,
          noneOfAbovePrompt,
          singleAnswerClarificationPrompt,
          connectToSupportPrompt
        } = skillSettings.disambiguation;

        let defaultConnectToSupportPrompt;
        if (!connectToSupportPrompt && skillSettings.launchMode?.enabled) {
          defaultConnectToSupportPrompt = 'Connect to support';
        }

        newSystemSettings.disambiguation = {
          ...systemSettings.disambiguation,
          enabled,
          prompt,
          none_of_the_above_prompt: noneOfAbovePrompt,
          single_answer_clarification_prompt: singleAnswerClarificationPrompt,
          use_connect_to_support_prompt:
            connectToSupportPrompt || defaultConnectToSupportPrompt
        };
      }
    }

    if (skillSettings.hasOwnProperty('launchMode')) {
      if (skillSettings.launchMode === null) {
        delete newSystemSettings.launch_mode;
      } else {
        const {
          enabled,
          default: defaultMode,
          learning,
          confident,
          no_action_matches
        } = skillSettings.launchMode;
        newSystemSettings.launch_mode = {
          ...systemSettings.launch_mode,
          confident,
          default: defaultMode,
          enabled,
          learning,
          no_action_matches
        };
      }
    }

    if (skillSettings.hasOwnProperty('infoGathering')) {
      newSystemSettings.generative_ai = skillSettings.infoGathering;
    }

    if (skillSettings.hasOwnProperty('displayFormats')) {
      const displayFormats = skillSettings?.displayFormats;

      if (displayFormats?.locale) {
        newSystemSettings.default_locale = displayFormats?.locale;
      } else if (displayFormats?.locale === null) {
        delete newSystemSettings?.default_locale;
      }

      newSystemSettings.variable = {
        format: {
          ...(displayFormats?.timeFormat && {
            time: { pattern: displayFormats?.timeFormat }
          }),
          ...(displayFormats?.dateFormat && {
            date: { pattern: displayFormats?.dateFormat }
          }),
          ...((displayFormats?.numberDelimiter ||
            displayFormats?.numberFraction) && {
            number: {
              delimiter: displayFormats?.numberDelimiter,
              ...(displayFormats?.numberFraction && {
                fraction_digits: displayFormats?.numberFraction
              })
            }
          }),
          ...((displayFormats?.currencySymbol ||
            displayFormats?.currencyFraction) && {
            currency: {
              symbol: displayFormats?.currencySymbol,
              ...(displayFormats?.currencyFraction && {
                fraction_digits: displayFormats?.currencyFraction
              })
            }
          }),
          ...(displayFormats?.percentFraction && {
            percent: {
              fraction_digits: displayFormats?.percentFraction,
              symbol: '%'
            }
          })
        }
      };
    }

    if (skillSettings.hasOwnProperty('topicSwitch')) {
      if (skillSettings.topicSwitch === null) {
        delete newSystemSettings.topic_switch;
      } else {
        const {
          enabled,
          messages = {},
          max_tries = 3,
          digression_handler
        } = skillSettings.topicSwitch;
        const actionDigressionEnabled =
          isActionsDigressionReturnConfirmationEnabled(getState());
        let messagesObj = {};
        if (actionDigressionEnabled) {
          const {
            enable_confirmation,
            confirmation_request,
            confirmation_buttons,
            confirmation_failure
          } = messages;
          const globalVariables = getGlobalVariables(getState()).map(
            v => v.variable
          );
          const systemVariables = getAllSystemVariables(getState()).map(
            v => v.system_variable
          );
          const { variables } = getState().models;
          let text_expression_request = confirmation_request;
          let text_expression_failure = confirmation_failure;

          //check if confirmation buttons are enabled already
          const alreadyHasEnabledButtons =
            confirmation_request?.generic?.filter(item => item.options).length >
            0;
          //convert request to text expression
          const expressionFormatRequest = {
            concat: splitTextIntoExpressionFormat(
              confirmation_request,
              globalVariables,
              systemVariables,
              null,
              variables
            )
          };
          text_expression_request = {
            generic: [
              {
                values: [{ text_expression: expressionFormatRequest }],
                response_type: 'text',
                selection_policy: 'sequential'
              }
            ]
          };
          //convert failure to text expression
          const expressionFormatFailure = {
            concat: splitTextIntoExpressionFormat(
              confirmation_failure,
              globalVariables,
              systemVariables,
              null,
              variables
            )
          };
          text_expression_failure = {
            generic: [
              {
                values: [{ text_expression: expressionFormatFailure }],
                response_type: 'text',
                selection_policy: 'sequential'
              }
            ]
          };

          if (alreadyHasEnabledButtons && !confirmation_buttons) {
            /* remove confirmation yes/no values from request
               if it was previously enabled and Tanya wants to disable it */
            const filterOutOptions = confirmation_request?.generic.filter(
              item => !item.options
            );
            text_expression_request = {
              ...text_expression_request,
              generic: filterOutOptions
            };
          } else if (!alreadyHasEnabledButtons && confirmation_buttons) {
            /* add confirmation yes/no values to request
               if it was previously disabled and Tanya wants to enable it */
            const yesNoTranslations = yesNoTranslation[language];
            // first item in yesNoTranslations is always the yes values
            const options = {
              options: [
                {
                  label: yesNoTranslations[0].value, // yes values
                  value: {
                    input: {
                      text: yesNoTranslations[0].value
                    }
                  }
                },
                {
                  label: yesNoTranslations[1].value,
                  value: {
                    input: {
                      text: yesNoTranslations[1].value // no values
                    }
                  }
                }
              ],
              response_type: 'option',
              repeat_on_reprompt: true
            };
            text_expression_request = {
              ...text_expression_request,
              generic: [...text_expression_request.generic, options]
            };
          }

          messagesObj = {
            enable_confirmation,
            confirmation_request: text_expression_request,
            confirmation_failure: text_expression_failure
          };
        }

        newSystemSettings.topic_switch = {
          enabled,
          messages: messagesObj,
          max_tries,
          digression_handler
        };

        /* Check if sys-yes-no entity is already added to the skill.
           If not, create the entity. This entity needs to be there
           for the digression confirmation to work */
      }
    }

    if (skillSettings.hasOwnProperty('autosave')) {
      dispatch(setAutoSaveState(skillSettings.autosave));
    }

    if (skillSettings.hasOwnProperty('algorithmVersions')) {
      const { model } = skillSettings.algorithmVersions;
      newSystemSettings = {
        ...newSystemSettings,
        nlp: {
          ...newSystemSettings.nlp,
          model
        }
      };
    }

    if (skillSettings.hasOwnProperty('autolearn')) {
      newSystemSettings = {
        ...newSystemSettings,
        ...skillSettings.autolearn
      };
    }

    if (skillSettings.hasOwnProperty('search_routing')) {
      newSystemSettings = {
        ...newSystemSettings,
        ...skillSettings.search_routing
      };
    }

    const updatedData = {
      skill_id,
      workspace: {
        system_settings: newSystemSettings
      }
    } as Skill;

    const state = getState();
    const skillId = getActionSkillId(state);
    const hasSysYesNoEntity = Boolean(getById(getState(), 'sys-yes-no'));
    const transactionOperations = [];
    const confirmationEnabled =
      updatedData?.workspace?.system_settings?.topic_switch?.messages
        ?.enable_confirmation;

    /* updates workspace settings in the skill */
    transactionOperations.push({
      operation: 'update',
      type: 'skill',
      target_reference: skillId,
      payload: { ...updatedData }
    });

    /* When confirmation topic is enabled, we need to make sure sys-yes-no entity is in the skill and
      digression_failure step is in the fallback action.
    */
    if (confirmationEnabled && !updatedData?.assistant_id) {
      const fallbackSteps = state.models.steps;

      /* Check if sys-yes-no entity is already added to the skill.
      If not, create the entity. This entity needs to be there
      for the digression confirmation to work */
      if (!hasSysYesNoEntity) {
        const languageYesOrNoValues = yesNoTranslation[language];
        transactionOperations.push({
          type: 'entity',
          operation: 'create',
          payload: {
            entity: 'sys-yes-no',
            operation: 'create',
            type: 'system',
            values: languageYesOrNoValues
          }
        });
      }
      /*
     Check if fallback action is in skill && digression_failure step is in the fallback action.
     If fallback action not in skill, do nothing but if its in skill but no digress_failure step,
     then the step needs to be created.
    */
      if (!_.isEmpty(fallbackSteps) && !fallbackSteps['digression_failure']) {
        const noTopicReturnStep = noTopicReturnTranslations[language];
        const steps = state.models.steps;
        const stepsArray = [];
        for (const i in steps) {
          stepsArray.push(steps[i]);
        }
        //if there are no fallback steps, remove `next_step` sicne its the ONLY step in the action
        if (stepsArray.length === 0) {
          delete noTopicReturnStep[0].next_step;
        } else {
          //find the first step id and assign it to the `next_step`
          const firstStepID = stepsArray[0].id;
          noTopicReturnStep[0].next_step = firstStepID;
        }

        const updatedSteps = [noTopicReturnStep[0], ...stepsArray];
        transactionOperations.push(
          {
            type: 'variable',
            operation: 'upsert',
            parent_references: { action: 'fallback' },
            payload: {
              variables: [
                {
                  data_type: 'any',
                  title: 'Failure to topic return',
                  variable: 'digression_failure'
                }
              ]
            }
          },
          {
            operation: 'update',
            type: 'action',
            payload: {
              action: 'fallback',
              condition: {
                intent: 'Failed to confirm topic return'
              },
              steps: updatedSteps
            },
            target_reference: 'fallback'
          }
        );
      }
    }

    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to sendTransactions
    const isNewIA = selectIsNewIa(state);
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(state);
    const assistantId = getAssistantId(state);

    const responseTransactions = await sendTransactions(
      skillId,
      {
        operations: transactionOperations
      },
      useNewIAUpdatedAccess,
      assistantId
    );

    if (confirmationEnabled && !hasSysYesNoEntity) {
      //fetch the latest entities if sys-yes-no was created in the above transactions call
      dispatch(
        fetchEntities(skillId, { page_limit: 100000 }, true, true, true)
      );
    }
    const data = mergeExistingData(updatedData, state, responseTransactions);
    return data;
  } catch (error) {
    dispatch(addErrorMessage(error));
    updatedObjectEvents.skill({
      object: skill_id,
      resultValue: getErrorString(error),
      successFlag: false
    });
    throw error;
  }
});

const mergeExistingData = (data, state, response) => {
  const { skill_id } = data;
  // The API only returns metadata and ignores workspace or actions content.
  // Merge the existing data with the new one and dispatch that
  // as the updated skill
  const oldSkill = selectSkillById(state, skill_id);
  const oldData = { ...data };
  if (oldSkill && oldSkill.workspace_reference) {
    if (data.workspace) {
      data.workspace_reference = _.merge(
        {},
        oldSkill.workspace_reference,
        data.workspace
      );

      if (oldData?.workspace?.system_settings?.variable) {
        // Display formats remove default values from the object.
        // To update the state we need to clear out the oldSkill data and place the new key/values in
        if (!oldData?.workspace?.system_settings?.default_locale) {
          delete data.workspace_reference.system_settings.default_locale;
        }
        data.workspace_reference.system_settings.variable =
          oldData.workspace.system_settings.variable;
      }

      if (oldData?.workspace?.system_settings?.topic_switch) {
        //make sure the topic switch remains the same in both places
        data.workspace_reference.system_settings.topic_switch =
          oldData.workspace.system_settings.topic_switch;
      }
    }
  }

  if (oldSkill?.type === SkillType.SEARCH) {
    // Search skills can only have settings for one of elastic, or discovery. If one exists on the old skill and the
    // response is coming back with the other then we need to clear the original from the oldSkill.
    if (
      oldSkill?.search_settings?.discovery &&
      response?.search_settings?.elastic_search
    ) {
      delete oldSkill.search_settings.discovery;
    } else if (
      oldSkill?.search_settings?.elastic_search &&
      response?.search_settings?.discovery
    ) {
      delete oldSkill.search_settings.elastic_search;
    }

    // If the search skill response is for elastic, and a password was sent to store, delete the password from state so
    // that it's no longer saved on our end.
    if (
      response?.search_settings?.elastic_search &&
      data.search_settings?.elastic_search?.password
    ) {
      delete data.search_settings.elastic_search.password;
    }
  }

  const updatedSkill = { ...oldSkill, ...data, ...response };

  updatedObjectEvents.skill({
    object: skill_id,
    resultValue: 'success',
    successFlag: true
  });
  return updatedSkill;
};

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(updateSkill.pending, (state, _action) => {
      state.updateStatus = ApiStatus.LOADING;
      state.updateError = null;
    })
    .addCase(updateSkill.fulfilled, (state, action) => {
      state.updateStatus = ApiStatus.SUCCEEDED;
      state.updateError = null;
      entityAdapter.upsertOne(state, action.payload);
    })
    .addCase(updateSkill.rejected, (state, action) => {
      state.updateStatus = ApiStatus.ERROR;
      state.updateError = action.error;
    })
    .addCase(updateActionSkillSettings.pending, (state, _action) => {
      state.updateStatus = ApiStatus.LOADING;
      state.updateError = null;
    })
    .addCase(updateActionSkillSettings.fulfilled, (state, action) => {
      state.updateStatus = ApiStatus.SUCCEEDED;
      state.updateError = null;
      entityAdapter.upsertOne(state, action.payload);
    })
    .addCase(updateActionSkillSettings.rejected, (state, action) => {
      state.updateStatus = ApiStatus.ERROR;
      state.updateError = action.error;
    });
};

export default reducer;
